import React, { useId, useMemo } from "react";
import {
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  addMinutes,
  browserTimeZone,
  describeRelative,
  describeWallClock,
  joinDateTime,
  nowWallClock,
  splitDateTime,
  wallClockToDate,
  zoneLabel,
} from "./dateTimeParts";
import "./datetime.css";

/**
 * Quick-fill chips, referenced by key so callers stay declarative.
 * `fromNow` offsets run from the current time; the rest run from `presetBase`
 * (e.g. an "Ends" field offsets from whatever "Starts" holds) and fall back to
 * now when that is empty.
 */
const PRESETS = {
  now: { label: "Now", minutes: 0, fromNow: true },
  "+1h": { label: "+1 hour", minutes: 60 },
  "+2h": { label: "+2 hours", minutes: 120 },
  "+4h": { label: "+4 hours", minutes: 240 },
  "+1d": { label: "+1 day", minutes: 1440 },
  "+3d": { label: "+3 days", minutes: 4320 },
  "+7d": { label: "+1 week", minutes: 10080 },
  "+14d": { label: "+2 weeks", minutes: 20160 },
  "+30d": { label: "+30 days", minutes: 43200 },
  "+90d": { label: "+90 days", minutes: 129600 },
};

/**
 * A date + quarter-hour time picker.
 *
 * Native `datetime-local` renders and behaves differently in every browser and
 * never names the zone it is in, so the time is split into a date input plus
 * hour / minute / AM-PM selects, and the current selection is echoed back in
 * plain language with its zone spelled out.
 *
 * `value` is a zone-less wall clock (`"2026-10-14T14:30"`), read as a clock
 * reading in `timeZone`. Converting to and from what the API stores is the
 * caller's job — see `instantToWallClock` / `wallClockToIso` in
 * `dateTimeParts`.
 */
const DateTimeField = ({
  id,
  label,
  value,
  onChange,
  timeZone,
  required = false,
  disabled = false,
  min,
  max,
  minMessage,
  maxMessage,
  presets = [],
  presetBase,
  allowClear = false,
  hint,
  error,
}) => {
  const reactId = useId();
  const fieldId = id || `ksdt-${reactId}`;
  const zone = timeZone || browserTimeZone();
  const parts = splitDateTime(value);

  const update = (patch) => onChange(joinDateTime({ ...parts, ...patch }));

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    const base =
      preset.fromNow || !presetBase ? nowWallClock(zone) : presetBase;
    onChange(preset.minutes ? addMinutes(base, preset.minutes, zone) : base);
  };

  const validation = useMemo(() => {
    if (!value) return "";
    if (min && value <= min) {
      return `Must be after ${minMessage || describeWallClock(min, zone)}.`;
    }
    if (max && value >= max) {
      return `Must be before ${maxMessage || describeWallClock(max, zone)}.`;
    }
    return "";
  }, [value, min, max, minMessage, maxMessage, zone]);

  const message = error || validation;
  const describedBy = message
    ? `${fieldId}-error`
    : value || hint
      ? `${fieldId}-echo`
      : undefined;

  const viewerZone = browserTimeZone();
  // An event held in another zone is typed in that zone, so the admin's own
  // clock reading is spelled out too rather than left to be worked out.
  const viewerTime =
    value && zone !== viewerZone
      ? new Intl.DateTimeFormat("en-US", {
          timeZone: viewerZone,
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        }).format(wallClockToDate(value, zone))
      : "";

  return (
    <div className={`ksdt${message ? " is-invalid" : ""}`}>
      <label className="ksdt__label" htmlFor={fieldId}>
        {label}
        {required && (
          <span className="ksdt__req" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div className="ksdt__controls">
        <input
          id={fieldId}
          className="ksdt__date"
          type="date"
          value={parts.date}
          disabled={disabled}
          required={required}
          aria-invalid={message ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => update({ date: e.target.value })}
        />
        {/* The three time controls wrap as one unit so they never split across lines. */}
        <div className="ksdt__time">
          <select
            className="ksdt__select"
            aria-label={`${label} — hour`}
            value={parts.hour}
            disabled={disabled}
            onChange={(e) => update({ hour: e.target.value })}
          >
            {HOUR_OPTIONS.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          <span className="ksdt__sep" aria-hidden="true">
            :
          </span>
          <select
            className="ksdt__select"
            aria-label={`${label} — minutes`}
            value={parts.minute}
            disabled={disabled}
            onChange={(e) => update({ minute: e.target.value })}
          >
            {MINUTE_OPTIONS.map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>
          <select
            className="ksdt__select"
            aria-label={`${label} — AM or PM`}
            value={parts.meridiem}
            disabled={disabled}
            onChange={(e) => update({ meridiem: e.target.value })}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>

      {(presets.length > 0 || (allowClear && value)) && (
        <div className="ksdt__presets">
          {presets.map((key) => (
            <button
              key={key}
              type="button"
              className="ksdt__preset"
              disabled={disabled}
              onClick={() => applyPreset(key)}
            >
              {PRESETS[key]?.label || key}
            </button>
          ))}
          {allowClear && value && (
            <button
              type="button"
              className="ksdt__preset"
              disabled={disabled}
              onClick={() => onChange("")}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {message ? (
        <div className="ksdt__error" id={`${fieldId}-error`}>
          {message}
        </div>
      ) : value ? (
        <div className="ksdt__echo" id={`${fieldId}-echo`}>
          <span className="ksdt__echo-strong">
            {describeWallClock(value, zone)}
          </span>
          <span className="ksdt__echo-muted">
            {" "}
            · {describeRelative(value, zone)}
          </span>
          {viewerTime && (
            <div className="ksdt__echo-muted">Your time: {viewerTime}</div>
          )}
        </div>
      ) : (
        <div className="ksdt__hint" id={`${fieldId}-echo`}>
          {hint || `Times are ${zoneLabel(zone)}.`}
        </div>
      )}

      {value && hint && !message && <div className="ksdt__hint">{hint}</div>}
    </div>
  );
};

export default DateTimeField;
